/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================
*/

import React, { useState } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Button,
  FormFeedback,
  Alert,
  Spinner
} from "reactstrap";
import { RiMailLine } from "react-icons/ri";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Api from "../components/helpers/Api";
import {SeoHelper} from "../components/helpers/SeoHelper";
import {debugLog} from "../utils/debug";

class ContactUs extends React.Component {

  render() {
    return (
        <>
          <SeoHelper
              uri="/contact-us"
              title="Contact Us | The Worship Bible"
              description={"Contact us to find out more about how we can help you build, manage and share worship song-flows based on scripture, themes, and the lectionary."}
          />

          <div className="position-relative">
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-default">
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Building your song-flows{" "}
                        <span>contact us</span>
                      </h1>
                    </Col>
                  </Row>
                </div>
              </Container>
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    version="1.1"
                    viewBox="0 0 2560 100"
                    x="0"
                    y="0"
                >
                  <polygon
                      className="fill-white"
                      points="2560 0 2560 100 0 100"
                  />
                </svg>
              </div>
            </section>
          </div>

          <section className="section section-lg pt-lg-0 mt--200">
            <Container>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Row className="row-grid">
                    <Col lg="12">
                      <Card className="card-lift--hover shadow border-0">
                        <CardBody className="py-2">
                          <Row>
                            <Col>
                              <h2 className="text-primary text-uppercase pt-3 pb-3" style={{fontSize: '1rem'}}>
                                <RiMailLine color='#5e72e4' size="36"/> Contact Us
                              </h2>
                            </Col>
                          </Row>
                          <Row>
                            <Col lg="12">
                              <ContactForm/>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </section>
        </>
    );
  }
};

// ✅ Updated Contact Form Component with reCAPTCHA, Rate Limiting, and Better Error Handling
function ContactForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // New state for rate limiting
  const [lastSubmitted, setLastSubmitted] = useState(0);
  const SUBMISSION_INTERVAL = 30000; // 30 seconds cooldown period

  // ✅ Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ✅ Validate the form before submission
  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
      valid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
      valid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // ✅ Submit the form with reCAPTCHA, rate limiting, and API integration
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check rate limiting: prevent submission if within the cooldown period
    const now = Date.now();
    if (now - lastSubmitted < SUBMISSION_INTERVAL) {
      setErrorMessage("Please wait a moment before sending another message.");
      return;
    }

    if (!validateForm()) return;

    if (!executeRecaptcha) {
      setErrorMessage("reCAPTCHA not available.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccess(false);

    try {
      const token = await executeRecaptcha("contact_form");

      const requestData = {
        ...formData,
        token,
      };

      const reCaptcha = window.env.RECAPTCHA_KEY || "not set"; // Fallback to localhost
      debugLog("ReCAPTCHA Site Key:", reCaptcha);

      // Await the response to ensure it is handled properly
      await Api.post("/contact", requestData).then((resp) => {
        debugLog("Full API response:", resp);

        if (resp && resp.status === 200 && resp.data) {
          debugLog("Success:", resp.data);
          setSuccess(true);
          setFormData({ name: "", email: "", message: "" });
          setLastSubmitted(Date.now()); // Update last submitted time
        } else {
          throw new Error("Unexpected API response.");
        }
      });
    } catch (error) {
      console.error("An error occurred:", error);

      if (error.response) {
        setErrorMessage(error.response.data || "Error sending message.");
      } else if (error.request) {
        setErrorMessage("No response from server. Please try again.");
      } else {
        setErrorMessage("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
      <>
        {success && <Alert color="primary">Thank you! Your message was sent successfully.</Alert>}
        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Input
                type="text"
                name="name"
                id="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                invalid={!!errors.name}
                className="form-control-alternative"
            />
            <FormFeedback>{errors.name}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Input
                type="email"
                name="email"
                id="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                invalid={!!errors.email}
                className="form-control-alternative"
            />
            <FormFeedback>{errors.email}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Input
                type="textarea"
                name="message"
                id="message"
                rows="4"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                invalid={!!errors.message}
                className="form-control-alternative"
            />
            <FormFeedback>{errors.message}</FormFeedback>
          </FormGroup>


            {loading
                ? <Spinner animation="border" role="status">
                    <span className="visually-hidden"></span>
                  </Spinner>
                : <Button color="primary" type="submit" disabled={loading}>
                    Send Message
                  </Button>
            }
        </Form>
      </>
  );
}

export default ContactUs;
